/**
 * O contexto de sessão: quem está logado, de que revenda, com que papel (H01).
 *
 * ── Uma máquina de estados, não três booleanos ────────────────────────────
 *
 * `carregando`, `autenticado` e `erro` como campos independentes admitem
 * combinações que não existem — carregando E autenticado, erro E autenticado —
 * e é assim que uma tela pisca conteúdo de outra pessoa por um quadro. Aqui há
 * um estado por vez, e o que a tela pode mostrar sai dele.
 *
 * ── O que decide o quê ────────────────────────────────────────────────────
 *
 * As permissões de `/v1/me` decidem o que a interface MOSTRA; o servidor decide
 * o que ela PODE. A H01 nomeia a armadilha: *"esconder o botão de excluir não
 * impede a chamada; a autorização vive no servidor (B16), e a interface só
 * reflete"*. Nada aqui é barreira de segurança — é redução de ruído.
 *
 * E o papel vem de `/v1/me`, nunca do claim do token: o claim congela na
 * emissão, então quem foi rebaixado continuaria com o papel antigo por até
 * quinze minutos. O `me.ts` do contracts explica isso do lado do servidor; a
 * consequência aqui é simples — não decodificar papel de token, jamais.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ErroDaApi, type Me } from '@cliqcrm/contracts';
import type { OrganizacaoDaPessoa } from '@cliqcrm/contracts';
import { criarClienteDaSessao } from '@/shared/api/cliente';
import { atrasoAteRenovar, ler, limpar, observarCofre, precisaRenovar } from './cofre';
import { ProvedorIndisponivelError } from './descoberta';
import { pessoaDoToken, type PessoaDoToken } from './pessoa-do-token';
import {
  iniciarLogin,
  reautenticarEmPopup,
  renovarSessao,
  sair as sairDoProvedor,
} from './fluxo-oidc';
import { estaEmbutido } from './embutido';
import { esquecerRevenda, lembrarRevenda, revendaAtual } from './revenda';

export type EstadoDaSessao =
  /** Ainda decidindo — só na primeira carga, e é o que a tela de espera cobre. */
  | { readonly nome: 'indeterminado' }
  /** Sem revenda escolhida, ou sem sessão no provedor. A tela de entrada aparece. */
  | { readonly nome: 'anonimo'; readonly revenda: string | null }
  /**
   * Provou quem é, e falta dizer ONDE entrar.
   *
   * O estado que o login em dois passos criou. O cofre já tem um token — o do
   * client genérico, que não carimba revenda — e ele serve exatamente uma
   * rota: a que produziu esta lista.
   *
   * Não é `autenticado` com um campo a menos, e por isso é estado próprio:
   * quem está aqui não tem papel, nem escopo, nem `me`. Qualquer tela do
   * produto renderizada neste momento leria `undefined` e mostraria uma casca
   * vazia.
   */
  | { readonly nome: 'escolhendo'; readonly revendas: readonly OrganizacaoDaPessoa[] }
  | { readonly nome: 'autenticado'; readonly revenda: string; readonly me: Me }
  /**
   * Tinha sessão e perdeu, COM a aplicação montada.
   *
   * Estado separado de `anonimo` de propósito: é o único em que existe trabalho
   * em andamento a preservar, e a interface responde com uma faixa que oferece
   * reentrar — não com a tela de login, que apagaria o que a pessoa escreveu.
   */
  | { readonly nome: 'expirado'; readonly revenda: string }
  /** Saiu daqui e do provedor. A tela confirma, e oferece entrar de novo. */
  | { readonly nome: 'saiu'; readonly revenda: string | null }
  /**
   * Um desfecho do login, com o motivo TIPADO: a tela de entrada tem uma
   * situação para cada um (`docs/design/login.md`). `motivo` é o texto
   * técnico, para o detalhe e para o log; `pessoa`, quem o provedor confirmou,
   * quando há token para ler.
   */
  | {
      readonly nome: 'erro';
      readonly tipo: 'revenda-nao-existe' | 'provedor-fora' | 'sem-vinculo' | 'desativado';
      readonly revenda: string | null;
      readonly motivo: string;
      readonly correlationId?: string;
      readonly pessoa?: PessoaDoToken;
    };

export interface Sessao {
  readonly estado: EstadoDaSessao;
  /**
   * Começa o login. Sem argumento é o primeiro passo — e-mail e senha.
   *
   * Com revenda, é o segundo: o mesmo realm, o client daquela revenda, e
   * nenhuma senha, porque a sessão no provedor já existe.
   */
  readonly entrar: (revenda?: string | null) => Promise<void>;
  /** Escolhe a revenda na segunda tela. */
  readonly escolherRevenda: (revenda: string) => Promise<void>;
  /** Reentra sem descarregar a aplicação. Ver `expirado`. */
  readonly reentrar: () => Promise<void>;
  readonly sair: () => Promise<void>;
  /**
   * A volta do provedor guardou uma credencial nova — reavalie a sessão.
   *
   * Sem isto, o primeiro login de uma pessoa terminava na tela de entrada: o
   * `/retorno` trocava o código por token e guardava, navegava para o destino,
   * e o estado da sessão continuava `anonimo` porque ninguém o releu. Sem erro
   * em lugar nenhum — o token estava lá, e a aplicação não sabia.
   *
   * Ficava escondido porque quem já tem cookie de SSO no provedor é recuperado
   * pela renovação silenciosa na primeira carga, e essa passa por
   * `carregarSessao`. O defeito só aparece em janela anônima, ou no primeiro
   * acesso de alguém — que é justamente quem não pode tropeçar.
   */
  readonly aoEstabelecerSessao: (revenda: string | null) => Promise<void>;
  /** Manda cadastrar segundo fator no provedor (B15). */
  readonly cadastrarSegundoFator: () => Promise<void>;
  /** `true` quando o login trouxe mais de um método no `amr`. Só interface. */
  readonly comSegundoFator: boolean;
  /** Volta à escolha de revenda, sem pedir senha — a sessão do provedor continua. */
  readonly trocarDeRevenda: () => void;
  /** Recomeça o login do zero, com a revenda conhecida se houver. */
  readonly recomecar: () => void;
  /**
   * A foto de perfil mudou (061): troca o link no `me` que a tela já tem.
   *
   * Reler `/v1/me` inteiro por causa de uma foto refaria as flags e as
   * preferências — e a resposta de trocar a foto já traz o link novo.
   */
  readonly definirFoto: (fotoUrl: string | null) => void;
  /**
   * Relê o `/v1/me` sem sair da tela (21/10/2026).
   *
   * O `me` é lido no login e guardado, e uma funcionalidade ligada, o papel ou
   * o acesso ao prontuário mudavam no servidor sem a tela saber: a aba nova
   * só aparecia depois de recarregar a página. Quem chama é o aviso
   * `sessao:eu` do fluxo e a tela que acabou de fazer a mudança.
   *
   * Falhar não derruba a sessão: o `me` que a tela tem continua valendo, e o
   * próximo aviso tenta de novo. Quem decide que a sessão acabou é a
   * renovação, não esta leitura.
   */
  readonly recarregarMe: () => Promise<void>;
}

/**
 * Exportado para o invólucro de teste da H14 poder fornecer uma sessão.
 *
 * A alternativa seria o teste montar o `ProvedorDeSessao` de verdade, que
 * tentaria falar com o provedor de identidade — e aí o teste de acessibilidade
 * passaria a depender de rede.
 */
export const ContextoDeSessao = createContext<Sessao | null>(null);

export function useSessao(): Sessao {
  const sessao = useContext(ContextoDeSessao);
  if (!sessao) throw new Error('useSessao precisa estar dentro de <ProvedorDeSessao>.');
  return sessao;
}

/**
 * Atalho de permissão.
 *
 * Existe para o componente não escrever `sessao.estado.nome === 'autenticado' &&
 * sessao.estado.me.papel === ...` toda vez — repetição que convida a esquecer o
 * primeiro termo e ler `me` de um estado que não o tem.
 */
export function usePapel(): Me['papel'] | null {
  const { estado } = useSessao();
  return estado.nome === 'autenticado' ? estado.me.papel : null;
}

/**
 * As preferências da revenda, pelo mesmo motivo de `usePapel` (H18).
 *
 * Sem sessão, os PADRÕES — e o padrão é conservador: `usar_documento_na_
 * identidade` volta `false`, que é o valor da coluna no banco (migração 002).
 * Na dúvida, a interface não pede documento.
 */
export function usePreferencias(): Me['preferencias'] {
  const { estado } = useSessao();
  return estado.nome === 'autenticado'
    ? estado.me.preferencias
    : { usar_documento_na_identidade: false, clinica: {} };
}

/** Sobrevive ao redirecionamento do logout, na mesma aba, e a mais nada. */
const MARCA_DE_SAIDA = 'cliqcrm:saiu';

const MENSAGEM_DE_FALHA =
  'Não foi possível confirmar sua sessão. Tente entrar de novo; se continuar, avise o suporte.';

export function ProvedorDeSessao({ children }: { children: ReactNode }): ReactNode {
  const [estado, definirEstado] = useState<EstadoDaSessao>({ nome: 'indeterminado' });
  const [comSegundoFator, definirSegundoFator] = useState(false);

  /**
   * A revenda vive numa ref, e não no estado.
   *
   * O agendador de renovação precisa dela e não pode ser recriado a cada
   * mudança de estado — recriar cancelaria e reagendaria o temporizador em toda
   * renderização, e um token de quinze minutos nunca chegaria a renovar.
   */
  const revenda = useRef<string | null>(null);

  const carregarSessao = useCallback(async (slug: string) => {
    try {
      const me = await criarClienteDaSessao(slug).me();
      definirSegundoFator((ler()?.amr.length ?? 0) > 1);
      definirEstado({ nome: 'autenticado', revenda: slug, me });
    } catch (erro: unknown) {
      // Token bom e `/v1/me` recusando: a API diz se o vínculo não existe ou
      // foi desativado — a extensão `vinculo` do 401. Qualquer outra recusa é
      // a API fora. Nos três casos a sessão não serve, e insistir mostraria uma
      // casca vazia. A pessoa é lida do token ANTES de limpar o cofre.
      const pessoa = pessoaDoToken(ler()?.idToken ?? null) ?? undefined;
      limpar();
      const vinculo = erro instanceof ErroDaApi ? erro.problema.vinculo : undefined;
      definirEstado({
        nome: 'erro',
        tipo:
          vinculo?.situacao === 'desativado'
            ? 'desativado'
            : vinculo
              ? 'sem-vinculo'
              : 'provedor-fora',
        revenda: slug,
        motivo: erro instanceof Error ? erro.message : MENSAGEM_DE_FALHA,
        ...(erro instanceof ErroDaApi ? { correlationId: erro.correlationId } : {}),
        ...(pessoa ? { pessoa } : {}),
      });
    }
  }, []);

  // ── Primeira carga ──────────────────────────────────────────────────────
  useEffect(() => {
    /*
     * Dentro de um iframe, esta instância não é a sessão de ninguém: ela é o
     * veículo de uma renovação que a aba principal começou. Tentar autenticar
     * aqui competiria com quem a criou — ver `estaEmbutido`.
     */
    if (estaEmbutido()) return;

    let cancelado = false;

    void (async () => {
      // Voltou do logout do provedor: a marca sobreviveu ao redirecionamento na
      // mesma aba, e a tela confirma que saiu — também do outro lado.
      if (sessionStorage.getItem(MARCA_DE_SAIDA) === '1') {
        sessionStorage.removeItem(MARCA_DE_SAIDA);
        if (!cancelado) definirEstado({ nome: 'saiu', revenda: revendaAtual() });
        return;
      }
      const slug = revendaAtual();
      revenda.current = slug;

      /*
       * Na volta do login, quem estabelece a sessão é o `/retorno`
       * (`aoEstabelecerSessao`), e o estado fica `indeterminado` até lá.
       *
       * Renovar aqui abria uma SEGUNDA autorização no mesmo client, enquanto o
       * código da primeira ainda era trocado — o provedor invalidava esse
       * código, e a pessoa via "O login está fora do ar" logo depois de digitar
       * a senha (13/10/2026). A falha da volta tem tela própria no `/retorno`.
       */
      if (location.pathname === '/retorno') return;

      if (!slug) {
        if (!cancelado) definirEstado({ nome: 'anonimo', revenda: null });
        return;
      }

      // Recarregou a página: o cofre é memória e sumiu. O cookie de SSO do
      // provedor não sumiu — é ele que devolve a sessão sem pedir senha.
      const recuperou = await renovarSessao(slug).catch(() => false);
      if (cancelado) return;

      if (recuperou) await carregarSessao(slug);
      else definirEstado({ nome: 'anonimo', revenda: slug });
    })();

    return () => {
      cancelado = true;
    };
  }, [carregarSessao]);

  // ── Renovação em segundo plano ──────────────────────────────────────────
  //
  // Reagendada a cada mudança do cofre: o instante da próxima renovação sai do
  // token que acabou de chegar, não de um intervalo fixo. Intervalo fixo erra
  // sempre que o provedor emite com duração diferente da configurada.
  useEffect(() => {
    // Mesma razão da primeira carga: o iframe de renovação não agenda nada.
    if (estaEmbutido()) return;

    let relogio: ReturnType<typeof setTimeout> | undefined;

    async function renovarAgora(): Promise<void> {
      const slug = revenda.current;
      if (!slug) return;

      // A trava compartilhada já tenta refresh e depois iframe, nessa
      // ordem — repetir a sequência aqui abriria dois fluxos de novo.
      const ok = await renovarSessao(slug).catch(() => false);

      // Falhou: NÃO derruba a aplicação nem redireciona. Vai para
      // `expirado`, que é o estado em que a faixa de reentrada aparece e o
      // que a pessoa estava escrevendo continua na tela.
      if (!ok) definirEstado({ nome: 'expirado', revenda: slug });
    }

    function agendar(): void {
      clearTimeout(relogio);
      const atraso = atrasoAteRenovar();
      if (!Number.isFinite(atraso)) return;
      relogio = setTimeout(() => void renovarAgora(), atraso);
    }

    /*
     * Voltar para a aba renova na hora, se o token estiver perto de vencer
     * (#637).
     *
     * O relógio acima não roda com o computador dormindo, a tampa fechada ou
     * a aba congelada pelo navegador para economizar energia. Na volta, ele
     * dispararia atrasado, ou só a primeira requisição descobriria o 401. Ao
     * voltar (aba visível, janela em foco, rede de novo), a renovação acontece
     * antes de qualquer requisição — e, com a sessão lembrada, o provedor
     * ainda devolve a sessão sem pedir senha.
     */
    function aoVoltar(): void {
      if (document.visibilityState !== 'visible') return;
      if (precisaRenovar()) void renovarAgora();
    }

    agendar();
    const parar = observarCofre(agendar);
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    window.addEventListener('online', aoVoltar);
    return () => {
      parar();
      clearTimeout(relogio);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
      window.removeEventListener('online', aoVoltar);
    };
  }, []);

  const entrar = useCallback(async (slug: string | null = null) => {
    /*
     * A revenda só é lembrada quando existe.
     *
     * No primeiro passo ela ainda vai ser escolhida, e gravar `null` aqui
     * apagaria a lembrança de quem estava entrando de novo na mesma revenda.
     */
    if (slug) lembrarRevenda(slug);
    revenda.current = slug;
    try {
      await iniciarLogin(slug);
    } catch (erro: unknown) {
      // O 404 da descoberta é o erro mais comum da tela de entrada, e tem nome
      // próprio; o resto é o provedor fora do ar.
      const naoExiste =
        erro instanceof ProvedorIndisponivelError &&
        erro.message.includes('esta organização não existe');
      definirEstado({
        nome: 'erro',
        tipo: naoExiste ? 'revenda-nao-existe' : 'provedor-fora',
        revenda: slug,
        motivo: erro instanceof Error ? erro.message : MENSAGEM_DE_FALHA,
      });
    }
  }, []);

  const reentrar = useCallback(async () => {
    const slug = revenda.current;
    if (!slug) return;

    // Popup primeiro: mantém a aplicação montada, e com ela o rascunho. Só se o
    // navegador bloquear é que se recorre ao redirecionamento, que descarrega
    // tudo — e aí o usuário já sabe o que vai acontecer, porque clicou no botão.
    if (await reautenticarEmPopup(slug)) await carregarSessao(slug);
    else await iniciarLogin(slug);
  }, [carregarSessao]);

  const sair = useCallback(async () => {
    const slug = revenda.current;
    esquecerRevenda();
    /*
     * Encerra no provedor sempre que há sessão, e não só com a revenda escolhida
     * (28/10). Na tela de escolher a organização ainda não há revenda, mas há o
     * token da primeira etapa e o cookie de SSO: limpar só aqui fazia o "Entrar
     * com outra conta" voltar à mesma escolha, sem pedir senha.
     */
    if (slug || ler()) {
      sessionStorage.setItem(MARCA_DE_SAIDA, '1');
      await sairDoProvedor(slug);
    } else {
      limpar();
      definirEstado({ nome: 'saiu', revenda: null });
    }
  }, []);

  /**
   * Trocar de revenda NÃO desloga.
   *
   * Era assim: esquecia o slug e voltava à tela de entrada, porque com um realm
   * por revenda trocar de revenda era trocar de CONTA — a decisão C3 dizia
   * exatamente isso, e a pessoa tinha duas senhas.
   *
   * Agora a conta é uma só e a revenda é um vínculo dela. Deslogar para trocar
   * seria pedir a senha de novo para exercer algo que a sessão já permite.
   *
   * O token atual serve para perguntar: `/v1/entrada/organizacoes` aceita os dois
   * — o do primeiro passo e o que já escolheu uma revenda.
   */
  /**
   * A segunda tela: em quais revendas esta pessoa entra.
   *
   * ── Os três desfechos, e por que só um deles pergunta ──────────────
   *
   * Nenhuma: a conta existe no provedor e não tem vínculo em lugar nenhum.
   * É `sem-vinculo`, o mesmo estado de quem foi desligado — e a tela dele já
   * explica o que fazer.
   *
   * Uma: entra direto. Perguntar com uma opção só é um clique que não decide
   * nada, e seria a experiência de quase toda revenda.
   *
   * Mais de uma: aí sim a tela aparece.
   */
  const carregarRevendas = useCallback(async () => {
    try {
      const { itens } = await criarClienteDaSessao(null).organizacoesDaPessoa();

      if (itens.length === 0) {
        const pessoa = pessoaDoToken(ler()?.idToken ?? null) ?? undefined;
        limpar();
        definirEstado({
          nome: 'erro',
          tipo: 'sem-vinculo',
          revenda: null,
          motivo: 'Esta conta não tem acesso a nenhuma organização.',
          ...(pessoa ? { pessoa } : {}),
        });
        return;
      }

      const unica = itens.length === 1 ? itens[0] : undefined;
      if (unica) {
        await entrar(unica.slug);
        return;
      }

      definirEstado({ nome: 'escolhendo', revendas: itens });
    } catch (erro: unknown) {
      limpar();
      definirEstado({
        nome: 'erro',
        tipo: 'provedor-fora',
        revenda: null,
        motivo: erro instanceof Error ? erro.message : MENSAGEM_DE_FALHA,
        ...(erro instanceof ErroDaApi ? { correlationId: erro.correlationId } : {}),
      });
    }
  }, [entrar]);

  const trocarDeRevenda = useCallback(() => {
    esquecerRevenda();
    revenda.current = null;
    void carregarRevendas();
  }, [carregarRevendas]);

  const recomecar = useCallback(() => {
    // Sem revenda conhecida, recomeçar é começar do primeiro passo — e ele
    // agora existe: `entrar()` sem argumento leva ao e-mail e senha.
    void entrar(revenda.current ?? revendaAtual());
  }, [entrar]);

  const cadastrarSegundoFator = useCallback(async () => {
    const slug = revenda.current;
    if (slug) await iniciarLogin(slug, location.pathname, 'CONFIGURE_TOTP');
  }, []);

  /**
   * Chamada pelo roteador quando `/retorno` conclui.
   *
   * `lembrarRevenda` junto porque o destino pode ser alcançado depois, numa
   * carga nova, e a revenda precisa sobreviver a isso.
   */
  const aoEstabelecerSessao = useCallback(
    async (slug: string | null) => {
      revenda.current = slug;

      /*
       * Sem revenda: acabou o PRIMEIRO passo.
       *
       * O cofre tem um token que prova quem a pessoa é e não diz onde ela
       * entra. Chamar `/v1/me` aqui seria pedir o tenant que a tela seguinte
       * ainda vai escolher — e a API recusaria, com razão.
       */
      if (!slug) {
        await carregarRevendas();
        return;
      }

      lembrarRevenda(slug);
      await carregarSessao(slug);
    },
    [carregarSessao, carregarRevendas],
  );

  /** O segundo passo: o client da revenda, sem senha de novo. */
  const escolherRevenda = useCallback(
    async (slug: string) => {
      await entrar(slug);
    },
    [entrar],
  );

  const recarregarMe = useCallback(async () => {
    const slug = revenda.current;
    if (!slug) return;
    try {
      const me = await criarClienteDaSessao(slug).me();
      // Só troca o `me` da MESMA sessão: se a pessoa saiu ou trocou de
      // organização enquanto a leitura voltava, a resposta é de outra.
      definirEstado((atual) =>
        atual.nome === 'autenticado' && atual.revenda === slug ? { ...atual, me } : atual,
      );
    } catch {
      // Ver o comentário da interface: falhar aqui não é motivo de sair.
    }
  }, []);

  const definirFoto = useCallback((fotoUrl: string | null) => {
    definirEstado((atual) =>
      atual.nome === 'autenticado'
        ? { ...atual, me: { ...atual.me, usuario: { ...atual.me.usuario, foto_url: fotoUrl } } }
        : atual,
    );
  }, []);

  const valor = useMemo<Sessao>(
    () => ({
      estado,
      entrar,
      escolherRevenda,
      reentrar,
      sair,
      aoEstabelecerSessao,
      cadastrarSegundoFator,
      comSegundoFator,
      trocarDeRevenda,
      recomecar,
      definirFoto,
      recarregarMe,
    }),
    [
      estado,
      entrar,
      reentrar,
      sair,
      aoEstabelecerSessao,
      cadastrarSegundoFator,
      comSegundoFator,
      trocarDeRevenda,
      recomecar,
      definirFoto,
      recarregarMe,
    ],
  );

  return <ContextoDeSessao.Provider value={valor}>{children}</ContextoDeSessao.Provider>;
}
